import type { LeadActivityMessageDelta } from "../utils/websocket";
import type { RouterOutputs } from "../trpc";

type InboxList = RouterOutputs["inbox"]["list"];

type RecentDelta = { delta: LeadActivityMessageDelta; expiresAt: number };

const RECENT_DELTA_TTL_MS = 15_000;

const recentDeltas = new Map<string, RecentDelta>();

export const recordRecentDelta = (leadId: string, delta: LeadActivityMessageDelta): void => {
  recentDeltas.set(leadId, { delta, expiresAt: Date.now() + RECENT_DELTA_TTL_MS });
};

export const clearRecentDeltaEntry = (leadId: string): void => {
  recentDeltas.delete(leadId);
};

export const clearAllRecentDeltas = (): void => {
  recentDeltas.clear();
};

const collectLiveDeltas = (): Map<string, LeadActivityMessageDelta> => {
  const now = Date.now();
  const live = new Map<string, LeadActivityMessageDelta>();
  for (const [leadId, entry] of recentDeltas) {
    if (entry.expiresAt <= now) {
      recentDeltas.delete(leadId);
      continue;
    }
    live.set(leadId, entry.delta);
  }
  return live;
};

export const applyDeltasToInboxList = <T extends InboxList | undefined>(input: T): T => {
  if (!input) return input;
  const live = collectLiveDeltas();
  if (live.size === 0) return input;
  let mutated = false;
  const nextData = input.data.map((row) => {
    const delta = live.get(row.id);
    if (!delta) return row;
    mutated = true;
    const isCustomer = delta.source === "customer";
    return {
      ...row,
      lastMessageContent: delta.content,
      lastMessageType: delta.type,
      lastMessageSource: delta.source,
      lastMessageReceived: delta.received,
      lastLeadMessageAt: isCustomer ? delta.createdAt : row.lastLeadMessageAt,
      unread: isCustomer ? true : row.unread,
      updatedAt: delta.createdAt,
    };
  });
  if (!mutated) return input;
  nextData.sort((a, b) => {
    const aAt = a.lastLeadMessageAt ?? a.createdAt;
    const bAt = b.lastLeadMessageAt ?? b.createdAt;
    return bAt.localeCompare(aAt);
  });
  return { ...input, data: nextData } as T;
};
